import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { ColumnDef } from '@tanstack/react-table';
import {
  Plus,
  Pencil,
  Trash2,
  Loader2,
  MessageSquare,
  Clock,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
// import toast from 'react-hot-toast';
import { TopBanner } from '@/components/TopBanner';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  useAutoMessages,
  useCreateAutoMessage,
  useUpdateAutoMessage,
  useDeleteAutoMessage,
} from '@/hooks/useAutoMessages';
import type { AutoMessage, AutoMessageTrigger } from '@/api/auto-messages.api';

export const Route = createFileRoute('/_authenticated/mensajes')({
  component: MensajesPage,
});

const TRIGGER_LABELS: Record<AutoMessageTrigger, string> = {
  on_signup: 'Registro',
  on_first_chat: 'Primer chat',
  inactive_24h: 'Inactivo 24h',
  after_deposit: 'Post-depósito',
  on_new_client: 'Nuevo cliente',
  on_first_deposit: 'Primer depósito',
  idle: 'Inactividad',
  manual: 'Manual',
};

const TRIGGER_VARIANTS: Record<AutoMessageTrigger, 'default' | 'secondary' | 'success' | 'warning' | 'destructive'> = {
  on_signup: 'default',
  on_first_chat: 'secondary',
  inactive_24h: 'warning',
  after_deposit: 'success',
  on_new_client: 'default',
  on_first_deposit: 'success',
  idle: 'warning',
  manual: 'secondary',
};

const TRIGGER_VALUES: AutoMessageTrigger[] = [
  'on_signup',
  'on_first_chat',
  'inactive_24h',
  'after_deposit',
  'on_new_client',
  'on_first_deposit',
  'idle',
  'manual',
];

const formSchema = z.object({
  trigger: z.enum([
    'on_signup',
    'on_first_chat',
    'inactive_24h',
    'after_deposit',
    'on_new_client',
    'on_first_deposit',
    'idle',
    'manual',
  ]),
  delaySeconds: z.coerce.number().int().min(0),
  template: z.string().min(1, 'El template es requerido').max(2000),
  isActive: z.boolean().default(true),
});

type FormValues = z.infer<typeof formSchema>;

function MensajesPage() {
  const { data: messages = [], isLoading } = useAutoMessages();
  const createMutation = useCreateAutoMessage();
  const updateMutation = useUpdateAutoMessage('');
  const deleteMutation = useDeleteAutoMessage();

  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<AutoMessage | null>(null);
  const [deleting, setDeleting] = useState<AutoMessage | null>(null);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      trigger: 'on_new_client',
      delaySeconds: 0,
      template: '',
      isActive: true,
    },
  });

  const onSubmit = async (values: FormValues) => {
    if (editing) {
      await updateMutation.mutateAsync({
        ...values,
        trigger: values.trigger,
      });
    } else {
      await createMutation.mutateAsync(values);
    }
    setIsOpen(false);
    setEditing(null);
    form.reset();
  };

  const handleEdit = (msg: AutoMessage) => {
    setEditing(msg);
    form.reset({
      trigger: msg.trigger,
      delaySeconds: msg.delaySeconds,
      template: msg.template,
      isActive: msg.isActive,
    });
    setIsOpen(true);
  };

  const handleDelete = async () => {
    if (!deleting) return;
    await deleteMutation.mutateAsync(deleting.id);
    setDeleting(null);
  };

  const columns = useMemo<ColumnDef<AutoMessage>[]>(
    () => [
      {
        accessorKey: 'trigger',
        header: 'Trigger',
        cell: ({ row }) => {
          const trigger = row.original.trigger;
          return (
            <Badge variant={TRIGGER_VARIANTS[trigger] || 'secondary'}>
              {TRIGGER_LABELS[trigger] || trigger}
            </Badge>
          );
        },
      },
      {
        accessorKey: 'template',
        header: 'Template',
        cell: ({ row }) => (
          <span className="text-sm max-w-[300px] truncate block" title={row.original.template}>
            {row.original.template}
          </span>
        ),
      },
      {
        accessorKey: 'delaySeconds',
        header: 'Delay',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.delaySeconds > 0 ? `${row.original.delaySeconds}s` : 'Inmediato'}
          </span>
        ),
      },
      {
        accessorKey: 'isActive',
        header: 'Estado',
        cell: ({ row }) => (
          <Badge variant={row.original.isActive ? 'default' : 'secondary'}>
            {row.original.isActive ? 'Activo' : 'Inactivo'}
          </Badge>
        ),
      },
      {
        accessorKey: 'createdAt',
        header: 'Creado',
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {new Date(row.original.createdAt).toLocaleDateString('es-AR')}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center gap-2">
            <Button size="icon" variant="ghost" onClick={() => handleEdit(row.original)}>
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="text-red-500"
              onClick={() => setDeleting(row.original)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const activeCount = messages.filter((m) => m.isActive).length;

  return (
    <>
      <TopBanner
        title="Mensajes"
        subtitle="Mensajes automáticos del tenant"
      />
      <div className="flex flex-wrap items-center justify-between gap-3 px-6 pt-4">
        <div className="text-sm text-muted-foreground">
          {messages.length} mensaje(s) en total
        </div>
        <Button
          onClick={() => {
            setEditing(null);
            form.reset({
              trigger: 'on_new_client',
              delaySeconds: 0,
              template: '',
              isActive: true,
            });
            setIsOpen(true);
          }}
        >
          <Plus className="mr-2 h-4 w-4" />
          Nuevo mensaje
        </Button>
      </div>
      <div className="flex-1 overflow-auto p-6">
        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4 mb-6">
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-primary/10 p-2.5">
                <MessageSquare className="h-5 w-5 text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold">{messages.length}</div>
                <div className="text-xs text-muted-foreground">Total mensajes</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-green-100 p-2.5">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">{activeCount}</div>
                <div className="text-xs text-muted-foreground">Activos</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-amber-100 p-2.5">
                <Clock className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {messages.filter((m) => m.delaySeconds > 0).length}
                </div>
                <div className="text-xs text-muted-foreground">Con delay</div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="flex items-center gap-4 p-4">
              <div className="rounded-full bg-red-100 p-2.5">
                <XCircle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <div className="text-2xl font-bold">
                  {messages.filter((m) => !m.isActive).length}
                </div>
                <div className="text-xs text-muted-foreground">Inactivos</div>
              </div>
            </CardContent>
          </Card>
        </div>

        <DataTable
          columns={columns}
          data={messages}
          isLoading={isLoading}
          emptyMessage="No hay mensajes automáticos configurados"
        />
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editing ? 'Editar mensaje' : 'Nuevo mensaje automático'}</DialogTitle>
            <DialogDescription>
              Configurá el trigger y el template del mensaje.
            </DialogDescription>
          </DialogHeader>
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              <FormField
                control={form.control}
                name="trigger"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Trigger</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {TRIGGER_VALUES.map((t) => (
                          <SelectItem key={t} value={t}>
                            {TRIGGER_LABELS[t]}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="delaySeconds"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Delay (segundos)</FormLabel>
                    <FormControl>
                      <Input type="number" min="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="template"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Template</FormLabel>
                    <FormControl>
                      <textarea
                        className="flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        placeholder="Hola {{nombre}}, bienvenido a..."
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                    <div className="space-y-0.5">
                      <FormLabel>Activo</FormLabel>
                      <p className="text-xs text-muted-foreground">El mensaje se enviará automáticamente</p>
                    </div>
                    <FormControl>
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="h-4 w-4"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
              <DialogFooter>
                <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
                  {(createMutation.isPending || updateMutation.isPending) && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Guardar
                </Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleting} onOpenChange={() => setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>¿Eliminar mensaje?</DialogTitle>
            <DialogDescription>
              {deleting && (
                <>
                  Se eliminará el mensaje con trigger <strong>{TRIGGER_LABELS[deleting.trigger]}</strong>.
                  Esta acción no se puede deshacer.
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete} disabled={deleteMutation.isPending}>
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Eliminar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
